import { CalendarModel, DateHelper, EventModelConfig, ProjectModel } from '@bryntum/schedulerpro';
import { EventModel } from '@bryntum/schedulerpro/schedulerpro.module';
import { format, isValid, parse } from 'date-fns';
import { isEmpty } from 'lodash';
import { Calendar } from 'sr-types/lib/search/v1/graphql';
import { toBryntumCalendar } from '../../../../scheduler/schedulerUtil';
import { roundUpByDurationUnit } from '../../../../supply/booking/ScheduleCalculator';
import { DEFAULT_LANG, TIME_CONSTANTS } from '../../../constants/constants';
import { API_DATE_FORMAT, API_TIME_FORMAT, mergeDateAndTime } from '../../../utils/dateTime';
import {
    DEFAULT_SCHEDULE_TIME_VALUE,
    SUPPORTED_SCHEDULER_DURATION_UNITS,
    SUPPORTED_SCHEDULER_TIME_DURATION_UNITS
} from '../constants';
import { ScheduleFormWidgetValue, SupportedSchedulerDurationUnit, SupportedSchedulerTimeDurationUnit } from '../types';
import {
    DurationValueRequiredError,
    EndDateRequiredError,
    InvalidDateTimeRangeError,
    InvalidDurationUnitError,
    InvalidStartDateTimeError,
    MixedDurationSpecifiedError,
    ScheduleCalculationFailedError
} from './errors';
import type { ScheduleCalculationInput, ScheduleCalculationResult } from './schedule-calculator';

/**
 * Normalizes time fields based on duration unit type.
 */
export function normalizeTimeFields(value: ScheduleFormWidgetValue): ScheduleFormWidgetValue {
    const isTimeBasedDurationUnit = checkIsTimeBasedDurationUnit(value.duration.unit);

    if (!isTimeBasedDurationUnit) {
        const normalizedStartTime = '';
        const normalizedEndTime = '';

        // Only create new object if values actually need to change
        if (normalizedStartTime === value.startTime && normalizedEndTime === value.endTime) {
            return value;
        }

        return {
            ...value,
            startTime: normalizedStartTime,
            endTime: normalizedEndTime
        };
    }

    // Set default time values if time-based but no time values exist
    const normalizedStartTime = value.startTime || DEFAULT_SCHEDULE_TIME_VALUE;
    const normalizedEndTime = value.endTime || DEFAULT_SCHEDULE_TIME_VALUE;

    if (normalizedStartTime === value.startTime && normalizedEndTime === value.endTime) {
        return value;
    }

    return {
        ...value,
        startTime: normalizedStartTime,
        endTime: normalizedEndTime
    };
}

/**
 * Normalizes duration value to ensure it's always a number or undefined.
 * FormWidget number component stores values as strings, so we convert them here.
 * Empty strings are converted to undefined since the field is optional.
 *
 * @todo: Move this logic in to the FormWidget number component,
 * behind an optional prop for backward compatibility.
 */
export function normalizeDurationValue(value: ScheduleFormWidgetValue): ScheduleFormWidgetValue {
    const durationValue = value.duration.value as unknown;
    let normalizedValue: number | undefined = undefined;

    if (typeof durationValue === 'number') {
        normalizedValue = durationValue;
    } else if (typeof durationValue === 'string') {
        const trimmed = durationValue.trim();
        if (trimmed !== '') {
            const numericValue = parseFloat(trimmed);
            normalizedValue = isNaN(numericValue) ? undefined : numericValue;
        }
    }

    // Only create new object if value actually changed
    if (normalizedValue === value.duration.value) {
        return value;
    }

    return {
        ...value,
        duration: {
            ...value.duration,
            value: normalizedValue
        }
    };
}

/**
 * Normalizes the calendar value by ensuring it references a valid calendar from the set loaded from the server.
 */
export function normalizeCalendarValue(value: ScheduleFormWidgetValue, calendarsData: Calendar[] | undefined) {
    if (value.calendar && calendarsData && calendarsData.length > 0) {
        const foundMatchingCalendarData = calendarsData.find((c) => c.id === value.calendar.id);
        if (foundMatchingCalendarData) {
            return {
                ...value,
                calendar: foundMatchingCalendarData
            };
        }
    }
    return value;
}

/**
 * Validates that all required inputs are present for schedule calculation.
 */
export function hasRequiredScheduleInputFields(value: ScheduleFormWidgetValue): boolean {
    if (value.calendar && !value.calendar.workingTime?.timeIntervals) {
        return false;
    }

    if (!value.duration.unit) {
        return false;
    }

    const isTimeBased = checkIsTimeBasedDurationUnit(value.duration.unit);

    if (!value.startDate) {
        return false;
    }

    if (isTimeBased && !value.startTime) {
        return false;
    }

    if (value.durationSpecified) {
        if (!(Number(value.duration.value) > 0)) {
            return false;
        }
    } else {
        if (!value.endDate) {
            return false;
        }

        if (isTimeBased && !value.endTime) {
            return false;
        }
    }

    return true;
}

/**
 * Converts ScheduleFormWidgetValue to ScheduleCalculationInput.
 * Extracts only the fields needed for schedule calculation.
 */
export function makeCalculationInputFromScheduleValue(
    value: ScheduleFormWidgetValue,
    locale?: string
): ScheduleCalculationInput {
    const base = {
        startDate: value.startDate,
        startTime: value.startTime,
        durationUnit: value.duration.unit,
        calendar: value.calendar,
        locale
    };

    if (value.durationSpecified) {
        return {
            ...base,
            durationSpecified: true,
            durationValue: value.duration.value!
        };
    } else {
        return {
            ...base,
            durationSpecified: false,
            endDate: value.endDate!,
            endTime: value.endTime
        };
    }
}

/**
 * Converts ScheduleCalculationResult to partial ScheduleFormWidgetValue.
 * Returns only the calculated schedule fields, not the entire widget value.
 */
export function makeScheduleValueFromCalculationResult(
    previousValue: ScheduleFormWidgetValue,
    result: ScheduleCalculationResult
): ScheduleFormWidgetValue {
    const isTimeBasedDurationUnit = checkIsTimeBasedDurationUnit(previousValue.duration.unit);
    return {
        ...previousValue,
        startDate: result.startDate,
        startTime: isTimeBasedDurationUnit ? result.startTime || DEFAULT_SCHEDULE_TIME_VALUE : '',
        endDate: result.endDate,
        endTime: isTimeBasedDurationUnit ? result.endTime || DEFAULT_SCHEDULE_TIME_VALUE : '',
        duration: {
            unit: result.durationUnit,
            value: result.durationValue
        }
    };
}

/**
 * Clears end date and time fields.
 */
export function clearEndDateTime(value: ScheduleFormWidgetValue): ScheduleFormWidgetValue {
    const clearedEndTime = checkIsTimeBasedDurationUnit(value.duration.unit) ? DEFAULT_SCHEDULE_TIME_VALUE : '';

    return {
        ...value,
        endDate: '',
        endTime: clearedEndTime
    };
}

/**
 * Clears the duration value.
 */
export function clearDurationValue(value: ScheduleFormWidgetValue): ScheduleFormWidgetValue {
    return {
        ...value,
        duration: {
            ...value.duration,
            value: undefined
        }
    };
}

/**
 * Clears calculated fields based on mode.
 * End date/time in duration mode, duration value in date-range mode.
 */
export function clearCalculatedFields(value: ScheduleFormWidgetValue): ScheduleFormWidgetValue {
    if (value.durationSpecified) {
        return clearEndDateTime(value);
    } else {
        return clearDurationValue(value);
    }
}

/**
 * Handles calculation errors by clearing appropriate fields based on error type.
 */
export function handleErrorAndClearCalculatedFields(
    error: unknown,
    value: ScheduleFormWidgetValue
): ScheduleFormWidgetValue {
    if (error instanceof InvalidStartDateTimeError) {
        return clearDurationValue(clearEndDateTime(value));
    } else if (error instanceof DurationValueRequiredError) {
        return clearEndDateTime(value);
    } else if (error instanceof EndDateRequiredError) {
        return clearDurationValue(value);
    } else {
        return clearCalculatedFields(value);
    }
}

export function makeSafeError(error: unknown, fallbackErrorMessage: string): Error {
    return error instanceof Error
        ? error
        : (error as { message: string }).message
          ? new Error((error as { message: string }).message)
          : typeof error === 'string'
            ? new Error(error)
            : new Error(fallbackErrorMessage);
}

export function validateAndParseScheduleCalculationInput(input: ScheduleCalculationInput, index?: number) {
    if (!('startDate' in input) || isEmpty(input.startDate)) {
        throw new InvalidStartDateTimeError('Schedule start date missing', index);
    }
    const parsedStartDate = parse(input.startDate, API_DATE_FORMAT, new Date());
    if (!isValid(parsedStartDate)) {
        throw new InvalidStartDateTimeError(`Failed to parse schedule start date ${input.startDate}`, index);
    }
    const parsedStartTime = input.startTime ? parse(input.startTime, API_TIME_FORMAT, new Date()) : undefined;
    const mergedStartDate = mergeDateAndTime(parsedStartDate, parsedStartTime);
    if (!isValid(mergedStartDate)) {
        throw new InvalidStartDateTimeError(
            `Failed to parse schedule start date/time ${input.startDate} ${input.startTime ?? ''}`,
            index
        );
    }

    if (
        !('durationUnit' in input) ||
        isEmpty(input.durationUnit) ||
        !SUPPORTED_SCHEDULER_DURATION_UNITS.includes(input.durationUnit)
    ) {
        throw new InvalidDurationUnitError(undefined, index, input.durationUnit);
    }
    const isTimeBasedDurationUnit = checkIsTimeBasedDurationUnit(input.durationUnit);

    if (input.durationSpecified) {
        if (
            input.durationValue === undefined ||
            input.durationValue === null ||
            typeof input.durationValue !== 'number'
        ) {
            throw new DurationValueRequiredError(undefined, index);
        }
        if (input.durationValue < 0) {
            throw new DurationValueRequiredError('Duration value cannot be less than zero', index);
        }

        const result = {
            durationSpecified: true as const,
            durationUnit: input.durationUnit,
            isTimeBasedDurationUnit,
            startDate: mergedStartDate,
            endDate: undefined as never,
            durationValue: input.durationValue,
            calendar: input.calendar,
            locale: input.locale ?? DEFAULT_LANG
        };

        return result;
    } else {
        if (!('endDate' in input) || isEmpty(input.endDate)) {
            throw new EndDateRequiredError('Schedule end date missing', index);
        }
        const parsedEndDate = parse(input.endDate, API_DATE_FORMAT, new Date());
        if (!isValid(parsedEndDate)) {
            throw new EndDateRequiredError(`Failed to parse schedule end date ${input.endDate}`, index);
        }
        const parsedEndTime = input.endTime ? parse(input.endTime, API_TIME_FORMAT, new Date()) : undefined;
        const mergedEndDate = mergeDateAndTime(parsedEndDate, parsedEndTime);
        if (!isValid(mergedEndDate)) {
            throw new EndDateRequiredError(
                `Failed to parse schedule end date/time ${input.endDate} ${input.endTime ?? ''}`,
                index
            );
        }
        if (mergedStartDate > mergedEndDate) {
            throw new InvalidDateTimeRangeError(undefined, index);
        }

        const result = {
            durationSpecified: false as const,
            durationUnit: input.durationUnit,
            isTimeBasedDurationUnit,
            startDate: mergedStartDate,
            endDate: mergedEndDate,
            durationValue: undefined as never,
            calendar: input.calendar,
            locale: input.locale ?? DEFAULT_LANG
        };

        return result;
    }
}

export function validateAndParseScheduleCalculationInputList(inputList: ScheduleCalculationInput[]) {
    let previousDurationSpecified: boolean | undefined = undefined;

    const resultList = inputList.map((input, index) => {
        const result = validateAndParseScheduleCalculationInput(input, index);
        if (index === 0) {
            previousDurationSpecified = result.durationSpecified;
        } else {
            if (result.durationSpecified !== previousDurationSpecified) {
                throw new MixedDurationSpecifiedError();
            }
        }
        return result;
    });

    return resultList;
}

export const getOrCreateBryntumCalendar = (project: ProjectModel, calendar?: Calendar): CalendarModel | undefined => {
    if (!calendar) {
        return undefined;
    }

    const bryntumCalendarModel = toBryntumCalendar(calendar);
    let bryntumCalendar = project.calendarManagerStore.getById(bryntumCalendarModel.id) as CalendarModel | undefined;

    if (!bryntumCalendar) {
        project.calendarManagerStore.add(bryntumCalendarModel);
        bryntumCalendar = project.calendarManagerStore.getById(bryntumCalendarModel.id) as CalendarModel;
    }

    return bryntumCalendar;
};

/**
 * Converts ScheduleCalculationInput to Bryntum event format.
 * Implements date adjustment logic for day-based durations.
 */
export function makesBryntumEventFromParsedScheduleCalculationInput(
    project: ProjectModel,
    input: ReturnType<typeof validateAndParseScheduleCalculationInput>,
    id: string
    /*,
    isSequenceFollower: boolean = false*/
): EventModelConfig {
    const eventModelConfig: EventModelConfig = {
        id,
        durationUnit: input.durationUnit
    };

    eventModelConfig.startDate = input.startDate;

    if (input.calendar) {
        const calendarModel = getOrCreateBryntumCalendar(project, input.calendar);
        if (calendarModel) {
            eventModelConfig.calendar = calendarModel;
        }
    }

    if (input.durationSpecified) {
        eventModelConfig.duration = input.durationValue;
        return eventModelConfig;
    }

    // Pre-adjustment: Add +1 day for day-based durations
    // This compensates for Bryntum's exclusive end-date semantics vs. our inclusive API/UI semantics
    let adjustedEndDate = input.endDate;
    if (!input.isTimeBasedDurationUnit) {
        adjustedEndDate = DateHelper.add(input.endDate, 1, TIME_CONSTANTS.d);
    }

    eventModelConfig.endDate = adjustedEndDate;

    return eventModelConfig;
}

/**
 * Extracts ScheduleCalculationResult from Bryntum event.
 * Implements date adjustment logic for day-based durations.
 */
export function makeScheduleCalculationResultFromBryntumEvent(
    project: ProjectModel,
    event: EventModelConfig,
    input: ReturnType<typeof validateAndParseScheduleCalculationInput>,
    index?: number
): ScheduleCalculationResult {
    const calculatedEvent = project.eventStore.getById(event.id) as EventModel;
    if (!calculatedEvent) {
        throw new ScheduleCalculationFailedError(undefined, index);
    }

    const durationUnit = calculatedEvent.durationUnit as SupportedSchedulerDurationUnit;

    const calculatedEndDate = calculatedEvent.endDate as Date;
    const calculatedDuration = calculatedEvent.duration;

    // Post-adjustment: Subtract -1 day for day-based durations
    const isTimeBasedDurationUnit = checkIsTimeBasedDurationUnit(durationUnit);
    let adjustedCalculatedEndDate = calculatedEndDate;
    if ((!input.durationSpecified || (input.durationSpecified && calculatedDuration > 0)) && !isTimeBasedDurationUnit) {
        adjustedCalculatedEndDate = DateHelper.add(calculatedEndDate, -1, TIME_CONSTANTS.d);
    }

    // Format results
    const formattedStartDate = format(input.startDate, API_DATE_FORMAT);
    const formattedStartTime = isTimeBasedDurationUnit ? format(input.startDate, API_TIME_FORMAT) : '';
    const formattedEndDate = format(adjustedCalculatedEndDate, API_DATE_FORMAT);
    const formattedEndTime = isTimeBasedDurationUnit ? format(adjustedCalculatedEndDate, API_TIME_FORMAT) : '';
    let formattedDurationValue = input.durationValue!;
    if (!input.durationSpecified) {
        formattedDurationValue = roundUpByDurationUnit(input.durationUnit, calculatedDuration, input.locale);
    }

    const result: ScheduleCalculationResult = {
        startDate: formattedStartDate,
        startTime: formattedStartTime,
        endDate: formattedEndDate,
        endTime: formattedEndTime,
        durationValue: formattedDurationValue,
        durationUnit
    };

    return result;
}

/**
 * Determines whether the time fields should be shown based on the selected duration unit.
 *
 * @param durationUnit The selected duration unit to check.
 * @returns Whether the duration unit should show time fields
 */
export function checkIsTimeBasedDurationUnit(
    durationUnit: SupportedSchedulerDurationUnit | undefined
): durationUnit is SupportedSchedulerTimeDurationUnit {
    return durationUnit !== undefined && SUPPORTED_SCHEDULER_TIME_DURATION_UNITS.includes(durationUnit);
}
